import React, { useState } from 'react';

export default function SavedReportsTree({ savedReports, onLoad, onDelete, onSave, canSave }) {
  const [expandedFolders, setExpandedFolders] = useState(new Set(['']));
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveFolder, setSaveFolder] = useState('');
  const [saving, setSaving] = useState(false);

  // Group reports by folder
  const folders = {};
  const rootReports = [];
  for (const r of savedReports) {
    if (r.folder) {
      if (!folders[r.folder]) folders[r.folder] = [];
      folders[r.folder].push(r);
    } else {
      rootReports.push(r);
    }
  }

  const allFolderNames = Object.keys(folders).sort();

  const toggleFolder = (folder) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      next.has(folder) ? next.delete(folder) : next.add(folder);
      return next;
    });
  };

  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      await onSave(saveName.trim(), saveFolder.trim() || null);
      setSaveName('');
      setSaveFolder('');
      setShowSaveForm(false);
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const ReportItem = ({ report }) => (
    <div className="group flex items-center gap-1 px-2 py-1 hover:bg-gray-100 rounded cursor-pointer">
      <svg className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
      <button
        onClick={() => onLoad(report)}
        className="flex-1 text-left text-xs text-gray-700 truncate"
        title={`${report.name}\nLast updated: ${new Date(report.updated_at).toLocaleDateString()}`}
      >
        {report.name}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(report.id); }}
        className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 p-0.5"
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className="text-sm">
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Saved Reports</span>
        {canSave && (
          <button
            onClick={() => setShowSaveForm(!showSaveForm)}
            className="text-xs text-primary-600 hover:text-primary-700"
            title="Save current report"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
        )}
      </div>

      {/* Save form */}
      {showSaveForm && (
        <div className="px-3 pb-2 space-y-1.5">
          <input
            type="text"
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            placeholder="Report name..."
            className="w-full border border-gray-300 rounded text-xs px-2 py-1"
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            autoFocus
          />
          <div className="flex gap-1">
            <select
              value={saveFolder}
              onChange={e => setSaveFolder(e.target.value)}
              className="flex-1 border border-gray-300 rounded text-xs px-2 py-1 bg-white"
            >
              <option value="">Root (no folder)</option>
              {allFolderNames.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <input
              type="text"
              value={saveFolder}
              onChange={e => setSaveFolder(e.target.value)}
              placeholder="or new folder..."
              className="flex-1 border border-gray-300 rounded text-xs px-2 py-1"
            />
          </div>
          <div className="flex gap-1">
            <button
              onClick={handleSave}
              disabled={saving || !saveName.trim()}
              className="flex-1 bg-primary-600 text-white rounded text-xs px-2 py-1 font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => { setShowSaveForm(false); setSaveName(''); setSaveFolder(''); }}
              className="text-gray-600 hover:text-gray-600 px-2 py-1 text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Folder tree */}
      <div className="px-1">
        {savedReports.length === 0 && !showSaveForm && (
          <p className="text-xs text-gray-600 italic px-2 py-1">No saved reports</p>
        )}

        {/* Folders */}
        {allFolderNames.map(folderName => {
          const isExpanded = expandedFolders.has(folderName);
          const reports = folders[folderName];
          return (
            <div key={folderName}>
              <button
                onClick={() => toggleFolder(folderName)}
                className="w-full flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 rounded"
              >
                <svg
                  className={`w-3 h-3 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
                <svg className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.5 21a3 3 0 003-3v-4.5a3 3 0 00-3-3h-15a3 3 0 00-3 3V18a3 3 0 003 3h15zM1.5 10.146V6a3 3 0 013-3h5.379a2.25 2.25 0 011.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 013 3v1.146A4.483 4.483 0 0019.5 9h-15a4.483 4.483 0 00-3 1.146z" />
                </svg>
                <span className="truncate font-medium">{folderName}</span>
                <span className="text-[10px] text-gray-600 ml-auto">{reports.length}</span>
              </button>
              {isExpanded && (
                <div className="ml-4">
                  {reports.map(r => <ReportItem key={r.id} report={r} />)}
                </div>
              )}
            </div>
          );
        })}

        {/* Root-level reports */}
        {rootReports.map(r => <ReportItem key={r.id} report={r} />)}
      </div>
    </div>
  );
}
